import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  IconButton,
  AppBar,
  Toolbar,
  TextField,
  Button,
  Paper,
  Stack,
  Divider,  
  Autocomplete,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Description as DescriptionIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import COLORS from '../../Utils/colors';
import { format } from 'date-fns';
import PaymentButton from '../../Components/PaymentButton';
import { request } from '../../Utils/request';
import { paiseToRupees } from '../../Utils/currencyConverter';

interface Guest {
  name: string;
  age: string;
  gender: 'male' | 'female' | '';
  contact: string;
  // email?: string;
}

interface BatchOption {
  label: string;
  id: string;
  batches?: any;
}

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const tripData = location.state?.tripData;
  const Type = location.state?.type;
  console.log('1234',Type)

  const [guests, setGuests] = useState<Guest[]>([
    { name: '', age: '', gender: '', contact: '' }
  ]);
  const [batchId, setBatchId] = useState<BatchOption | null>(null);
  const [showPriceDetails, setShowPriceDetails] = useState(false);

  const handleBack = () => {
    window.history.back();
  };

  const confirmBooking = async (razorpay_payment_id: string) => {
  if (!batchId) {
    console.error("No batches selected");
    return;
  }
  
  const res = await request<{ data:any, razorpayOrderId: string }>(`/bookings/trips/${tripData?._id}`, {
    method: 'POST',
    body: {
      "batchId": batchId?.id,
      "quantity": guests?.length,
      "userInfo": guests,
      // "paymentId": razorpay_payment_id
  },
    authorization: true,
  }).then((res: any) => {
    if(res?.success) {
      console.log("confirmBooking", res);
      
      /**
       * STACK CLEAR REQUIRED HERE:
       * After successful booking/payment completion, we clear the navigation stack to:
       * 1. Prevent users from going back to the checkout form using browser back button
       * 2. Avoid accidental duplicate bookings/payments if user navigates back
       * 3. Ensure the payment form is not resubmitted accidentally
       * 4. Navigate back to the trip detail page to show booking confirmation
       * 5. Protect against potential security issues with cached payment data
       * 
       * Using replace: true instead of push ensures checkout page is removed from history
       */
      // Navigate back to the trip detail page after successful booking
      navigate(`/trips/${tripData?._id}?type=${Type}`, { 
        replace: true,
        state: { type: Type }
      });
    } else {
      console.log("confirmBooking error", res);
    }
  }).catch((err) => {
    console.log("confirmBooking error", err);
  });
  console.log("confirmBooking", res);
}

  const handleAddGuest = () => {
    if (guests.length < 5) {
      setGuests([...guests, { name: '', age: '', gender: '', contact: '' }]);
    }
  };

  const handleGuestChange = (index: number, field: keyof Guest, value: string) => {
    const newGuests = [...guests];
    newGuests[index] = { ...newGuests[index], [field]: value };
    setGuests(newGuests);
  };

  const handleDeleteGuest = (index: number) => {
    if (guests.length > 1) {
      const newGuests = guests.filter((_, i) => i !== index);
      setGuests(newGuests);
    }
  };

  // Extract appConfig from trip data
  const appConfig = tripData?.appConfig || tripData?.appconfig || {};



  const pricePerPerson = tripData?.pricePerPerson ;
  
  // Standardized calculation system
  const totalcharges = pricePerPerson * guests.length ;

  // GST calculation (standard practice: GST on base amount)
  const gst = totalcharges * ((appConfig.gst) / 100);

  const subtotal = totalcharges + gst;
  
  // Payment Gateway Fee (standard practice: fee on base amount)
  const paymentGatewayFee = subtotal * ((appConfig.paymentGatewayFee ) / 100);
  
  // Payment Gateway GST (standard practice: GST on gateway fee)
  const gstOnPaymentGatewayFee = paymentGatewayFee * ((appConfig.paymentGatewayGst ) / 100);
  
  // Total taxes and charges
  // const totalTaxesAndCharges = gst + paymentGatewayFee + gstOnPaymentGatewayFee;
  
  // Final total
  const totalPrice = subtotal + paymentGatewayFee + gstOnPaymentGatewayFee;
  
  // Helper function for consistent number formatting
  const formatCurrency = (amount: number) => `₹${amount.toFixed(2)}`;
  
  // Validation: Ensure all calculations are valid numbers
  const isValidCalculation = (amount: number) => !isNaN(amount) && isFinite(amount);
  
  // Validate all calculated amounts
  if (!isValidCalculation(totalcharges) || !isValidCalculation(totalPrice)) {
    console.error('Invalid calculation detected:', { totalcharges, totalPrice, pricePerPerson, guests: guests.length });
  }

  // Check if at least one guest has complete details
  const hasValidGuest = guests.some(guest => 
    guest.name.trim() && 
    guest.age && 
    guest.gender && 
    guest.contact.trim()
  );

  // Check if batches are selected
  const hasValidBatch = batchId !== null ;

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>

      {/* Navigation Bar */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'white' }}>
        <Toolbar sx={{ minHeight: 56 }}>
          <IconButton edge="start" onClick={handleBack} sx={{ color: '#000' }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ color: '#000', ml: 1, fontWeight: 500 }}>
            Checkout
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ py: 2 }}>
        {/* Trip Details */}   
        {Type === 'trip' &&
        <Box sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Trip to {tripData?.title}
          </Typography>
          
          <Autocomplete
            fullWidth
            options={tripData.batches.map((batch: any) => ({
              label: `${format(new Date(batch.from), "do MMMM yy")} - ${format(new Date(batch.to), "do MMMM yy")}`,
              id: batch._id || batch.id,
              batches: batch
            }))}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => <TextField {...params} label="Select trip date" />}
            value={batchId}
            onChange={(e, value) => {
              if (value) {
                setBatchId({
                  label: value.label,
                  id: value.id,
                  batches: value.batches
                });
                console.log("Selected batches:", value);
              } else {
                setBatchId(null);
              }
            }}
            sx={{ mt: 1 }}  
            />
          {!hasValidBatch && batchId === null && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              Please select a trip date to continue
            </Typography>
          )}
        </Box>
        } 

        {/* Personal Details */}
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Personal details
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Max of 5 guest can be added for this trip
          </Typography>

          {guests.map((guest, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight="bold" color={COLORS.primary}>
                  Guest {index + 1}
                </Typography>
                {index > 0 && (
                  <IconButton
                    onClick={() => handleDeleteGuest(index)}
                    sx={{ color: 'error.main' }}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Stack>
              <TextField
                fullWidth
                label="Enter Name"
                value={guest.name}
                onChange={(e) => handleGuestChange(index, 'name', e.target.value)}
                sx={{ mb: 2 }}
              />
              
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Autocomplete
                  fullWidth
                  options={Array.from({ length: 100 }, (_, i) => i + 1).map((age) => age.toString())}
                  renderInput={(params) => <TextField {...params} label="Age" />}
                  value={guest.age}
                  onChange={(e, value) => handleGuestChange(index, 'age', value || '')}
                />
                
                <Stack direction="row" spacing={1}>
                  <Button
                    variant={guest.gender === 'male' ? 'contained' : 'outlined'}
                    onClick={() => handleGuestChange(index, 'gender', 'male')}
                    sx={{
                      minWidth: 80,
                      bgcolor: guest.gender === 'male' ? COLORS.primary : 'transparent',
                      color: guest.gender === 'male' ? 'white' : COLORS.primary,
                      borderColor: COLORS.primary,
                      '&:hover': {
                        bgcolor: guest.gender === 'male' ? COLORS.primary : 'transparent',
                      }
                    }}
                  >
                    Male
                  </Button>
                  <Button
                    variant={guest.gender === 'female' ? 'contained' : 'outlined'}
                    onClick={() => handleGuestChange(index, 'gender', 'female')}
                    sx={{
                      minWidth: 80,
                      bgcolor: guest.gender === 'female' ? COLORS.primary : 'transparent',
                      color: guest.gender === 'female' ? 'white' : COLORS.primary,
                      borderColor: COLORS.primary,
                      '&:hover': {
                        bgcolor: guest.gender === 'female' ? COLORS.primary : 'transparent',
                      }
                    }}
                  >
                    Female
                  </Button>
                </Stack>
              </Stack>
              
              <TextField
                fullWidth
                label="Contact no."
                value={guest.contact}
                onChange={(e) => handleGuestChange(index, 'contact', e.target.value)}
                sx={{ mb: 2 }}
              />

              {/* <TextField
                fullWidth
                label="Email"
                value={guest.email}
                onChange={(e) => handleGuestChange(index, 'email', e.target.value)}
                sx={{ mb: 2 }}
              /> */}
            </Box>
          ))}

          {guests.length < 5 && (
            <Button
              variant="text"
              onClick={handleAddGuest}
              sx={{ color: COLORS.primary, textTransform: 'none' }}
            >
              Add guest
            </Button>
          )}
        </Paper>

        {/* Price Details */}
        <Paper 
          sx={{ 
            p: 2, 
            mb: 2, 
            borderRadius: 2, 
            bgcolor: '#FFF3E0',
            cursor: 'pointer'
          }}
          onClick={() => setShowPriceDetails(!showPriceDetails)}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <DescriptionIcon sx={{ color: COLORS.primary }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" fontWeight="bold">
                To Pay {paiseToRupees(totalPrice)}             </Typography>
              <Typography variant="body2" color="text.secondary">
                {gst > 0 ? `Incl. GST, PG fees & charges` : 'Incl. all taxes & charges'}
              </Typography>
            </Box>
            {showPriceDetails ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </Stack>
          
          {showPriceDetails && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
              {/* Base Price Breakdown */}
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="body2">Base Price</Typography>
                <Typography variant="body2">{paiseToRupees(pricePerPerson)} × {guests.length} guests</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="body2">Subtotal</Typography>
                <Typography variant="body2">{paiseToRupees(subtotal)}</Typography>
              </Stack>
              {/* Taxes and Charges Breakdown */}
              {appConfig.gst > 0 && (
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="body2">GST ({appConfig.gst}%)</Typography>
                  <Typography variant="body2">{paiseToRupees(gst)}</Typography>
                </Stack>
              )}
              {appConfig.paymentGatewayFee > 0 && (
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="body2">Payment Gateway Fee ({appConfig.paymentGatewayFee}%)</Typography>
                  <Typography variant="body2">{paiseToRupees(paymentGatewayFee)}</Typography>
                </Stack>
              )}
              {appConfig.paymentGatewayGst > 0 && (
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="body2">PG GST ({appConfig.paymentGatewayGst}%)</Typography>
                  <Typography variant="body2">{paiseToRupees(gstOnPaymentGatewayFee)}</Typography>
                </Stack>
              )}
              
              {/* Summary */}
                <Typography variant="body2">Total Taxes & Charges</Typography>
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="h6" fontWeight="bold">Total Amount</Typography>
                <Typography variant="h6" fontWeight="bold">{paiseToRupees(totalPrice)}</Typography>
              </Stack>
            </Box>
          )}
        </Paper>
        <PaymentButton
          amount={paiseToRupees(totalPrice)}
          currency="INR"
          paymentFor={Type === 'trip'? "trip_booking":'itinerary_purchase'}
          referenceId={tripData?._id}
          userData={{
            name: guests[0].name,
            // email: guests[0].email || '',
            contact: guests[0].contact,
          }}
          onPaymentSuccess={(razorpay_payment_id) => {
            console.log("razorpay_payment_id in checkout", razorpay_payment_id);
            confirmBooking(razorpay_payment_id);
          }}
          onPaymentError={() => {}}
          buttonText="Pay Now"
          disabled={((!hasValidGuest || !hasValidBatch) && Type !== 'itinerary') ? true:false}
          sx={{ mt: 3 }}
        />
      </Container>
    </Box>
  );
};

export default Checkout;
